import { Boundary, Point } from '../interface';

function clearBitmapBuffer(bitmap: Array<boolean>): void {
  for (let i = 0; i < bitmap.length; i++) {
    bitmap[i] = false;
  }
}

function drawLinesBitmapBuffer(
  points: Array<Point>,
  bitmap: Array<boolean>,
  width: number,
  height: number,
  stroke: number
): void {
  const boundary: Boundary<Point> = getPointsBoundary(points);

  for (let i = 1; i < points.length; i++) {
    const normalizedPoint: Point = getNormalizedPoint(boundary, points[i], width, height, stroke);
    const prevNormalizedPoint: Point = getNormalizedPoint(boundary, points[i - 1], width, height, stroke);

    drawLineToBitmap(normalizedPoint, prevNormalizedPoint, bitmap, width, height, stroke);
  }
}

function drawLineToBitmap(
  point: Point,
  prevPoint: Point,
  bitmap: Array<boolean>,
  width: number,
  height: number,
  stroke: number
): void {
  if (Math.abs(point.y - prevPoint.y) >= Math.abs(point.x - prevPoint.x)) {
    const inclination = (point.x - prevPoint.x) / (point.y - prevPoint.y);

    if (prevPoint.y < point.y) {
      for (let y = 0; y <= point.y - prevPoint.y; y++) {
        const position = {
          y: Math.floor(prevPoint.y + y + stroke),
          x: Math.floor(prevPoint.x + inclination * y + stroke),
        };
        drawPoint(position, bitmap, width, height, stroke);
      }
    } else {
      for (let y = 0; y <= prevPoint.y - point.y; y++) {
        const position = {
          y: Math.floor(prevPoint.y - y + stroke),
          x: Math.floor(prevPoint.x - inclination * y + stroke),
        };
        drawPoint(position, bitmap, width, height, stroke);
      }
    }
  } else {
    const inclination = (point.y - prevPoint.y) / (point.x - prevPoint.x);

    if (prevPoint.x < point.x) {
      for (let x = 0; x <= point.x - prevPoint.x; x++) {
        const position = {
          y: Math.floor(prevPoint.y + inclination * x + stroke),
          x: Math.floor(prevPoint.x + x + stroke),
        };
        drawPoint(position, bitmap, width, height, stroke);
      }
    } else {
      for (let x = 0; x <= prevPoint.x - point.x; x++) {
        const position = {
          y: Math.floor(prevPoint.y - inclination * x + stroke),
          x: Math.floor(prevPoint.x - x + stroke),
        };
        drawPoint(position, bitmap, width, height, stroke);
      }
    }
  }
}

function drawPoint(point: Point, bitmap: Array<boolean>, width: number, height: number, radius: number = 0): void {
  for (let y = point.y - radius; y <= point.y + radius; y++) {
    for (let x = point.x - radius; x <= point.x + radius; x++) {
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      const r = Math.sqrt((x - point.x) ** 2 + (y - point.y) ** 2);
      if (r <= radius) {
        bitmap[y * width + x] = true;
      }
    }
  }
}

function getPointsBoundary(points: Array<Point>): Boundary<Point> {
  const boundary: Boundary<Point> = {
    min: { x: Infinity, y: Infinity },
    max: { x: -Infinity, y: -Infinity },
  };

  for (const point of points) {
    if (boundary.min.x > point.x) boundary.min.x = point.x;
    if (boundary.min.y > point.y) boundary.min.y = point.y;
    if (boundary.max.x < point.x) boundary.max.x = point.x;
    if (boundary.max.y < point.y) boundary.max.y = point.y;
  }

  if (boundary.max.x - boundary.min.x < boundary.max.y - boundary.min.y) {
    boundary.min.x -= (boundary.max.y - boundary.min.y - (boundary.max.x - boundary.min.x)) / 2;
    boundary.max.x += (boundary.max.y - boundary.min.y - (boundary.max.x - boundary.min.x)) / 2;
  } else {
    boundary.min.y -= (boundary.max.x - boundary.min.x - (boundary.max.y - boundary.min.y)) / 2;
    boundary.max.y += (boundary.max.x - boundary.min.x - (boundary.max.y - boundary.min.y)) / 2;
  }

  return boundary;
}

function getNormalizedPoint(
  boundary: Boundary<Point>,
  point: Point,
  width: number,
  height: number,
  stroke: number
): Point {
  return {
    x: ((point.x - boundary.min.x) / (boundary.max.x - boundary.min.x || 1)) * (width - stroke * 2),
    y: ((point.y - boundary.min.y) / (boundary.max.y - boundary.min.y || 1)) * (height - stroke * 2),
  };
}

function get32BitRGBA(r: number, g: number, b: number, a: number): number {
  return (a << 24) + (b << 16) + (g << 8) + r;
}

function compareBitmapBuffer(bitmap: Array<boolean>, answer: Array<boolean>): number {
  let diff = 0;
  let total = 0;

  for (let i = 0; i < answer.length; i++) {
    diff += bitmap[i] !== answer[i] ? 1 : 0;
    total += bitmap[i] || answer[i] ? 1 : 0;
  }

  return 1 - diff / total;
}

export { clearBitmapBuffer, drawLinesBitmapBuffer, compareBitmapBuffer, get32BitRGBA };
